// frontend/src/pages/Incidents.jsx
// Module 8 alignment: add 3 new incident types (CPS234-AI, AUSTRAC, FAR),
//                     fix getOrgUsers → getUsers (Module 8 API).
// commit: fix(m8): expand incident taxonomy for CPS234-AI, AUSTRAC, FAR obligations; fix user lookup

import React, { useEffect, useState, useRef } from 'react';
import {
  getIncidents,
  createIncident,
  updateIncident,
  deleteIncident,
  uploadIncidentAttachment,
  getVendors,
  getUsers,           // Module 8 — replaces missing getOrgUsers
} from '../api';

// ---------------------------------------------------------------------------
// Taxonomy — original 5 + 3 new types aligned with migration 009 obligations
// ---------------------------------------------------------------------------
const INCIDENT_TYPES = [
  // Original CPS 230 types
  { value: 'model_failure',            label: 'Model Failure',                         group: 'CPS 230' },
  { value: 'data_leak_via_ai_vendor',  label: 'Data Leak via AI Vendor',               group: 'CPS 230' },
  { value: 'bias_complaint',           label: 'Bias / Fairness Complaint',             group: 'CPS 230' },
  { value: 'hallucination_event',      label: 'Hallucination Event',                   group: 'CPS 230' },
  { value: 'unauthorised_ai_tool_use', label: 'Unauthorised AI Tool Use',              group: 'CPS 230' },
  // NEW: CPS 234 AI security
  { value: 'ai_security_breach',       label: 'AI Security Breach',                    group: 'CPS 234' },
  // NEW: AUSTRAC AML/CTF
  { value: 'aml_ctf_model_failure',    label: 'AML/CTF Model Failure',                 group: 'AUSTRAC' },
  // NEW: FAR accountability
  { value: 'accountability_breach',    label: 'FAR Accountability Breach',             group: 'FAR' },
];

const INCIDENT_TYPE_LABELS = Object.fromEntries(INCIDENT_TYPES.map(t => [t.value, t.label]));

const SEVERITIES = [
  { value: 'critical', label: 'Critical', bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a' },
  { value: 'high',     label: 'High',     bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  { value: 'medium',   label: 'Medium',   bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
  { value: 'low',      label: 'Low',      bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
];

const STATUSES = [
  { value: 'open',         label: 'Open',         bg: '#fce4ec', color: '#c62828', border: '#ef9a9a' },
  { value: 'under_review', label: 'Under Review', bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
  { value: 'closed',       label: 'Closed',       bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
];

// CPS 234 AI security breach and AUSTRAC failures are also subject to mandatory notification
const NOTIFICATION_REQUIRED_TYPES = [
  'model_failure',
  'data_leak_via_ai_vendor',
  'ai_security_breach',
  'aml_ctf_model_failure',
  'accountability_breach',
];

function severityCfg(value) { return SEVERITIES.find(s => s.value === value) || SEVERITIES[3]; }
function statusCfg(value)   { return STATUSES.find(s => s.value === value)   || STATUSES[0]; }

function cps230Timeline(incident) {
  if (!incident.cps230_notification_required) return null;
  if (!['critical', 'high'].includes(incident.severity)) return null;
  const occurred  = new Date(incident.occurred_at);
  const now       = new Date();
  const hElapsed  = Math.floor((now - occurred) / (1000 * 60 * 60));
  const deadline  = 72;
  const hRemaining = deadline - hElapsed;
  const overdue   = hElapsed >= deadline;
  const notified  = !!incident.notified_at;
  let label, labelColor;
  if (notified) {
    label = `Notified — ${new Date(incident.notified_at).toLocaleDateString('en-AU')}`;
    labelColor = '#2e7d32';
  } else if (overdue) {
    label = `CPS 230 notification overdue by ${Math.abs(hRemaining)}h`;
    labelColor = '#b71c1c';
  } else {
    label = `CPS 230 notification due in ${hRemaining}h`;
    labelColor = hRemaining <= 12 ? '#e65100' : '#f57f17';
  }
  return { hElapsed, deadline, overdue, notified, hRemaining, label, labelColor };
}

function SeverityBadge({ severity }) {
  const cfg = severityCfg(severity);
  return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap', letterSpacing: 0.2 }}>{cfg.label}</span>;
}

function StatusBadge({ status }) {
  const cfg = statusCfg(status);
  return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
}

function Cps230Bar({ incident }) {
  const tl = cps230Timeline(incident);
  if (!tl) return null;
  const pct = Math.min(100, Math.round((tl.hElapsed / tl.deadline) * 100));
  const barColor = tl.notified ? '#2e7d32' : tl.overdue ? '#b71c1c' : tl.hRemaining <= 12 ? '#e65100' : '#f57f17';
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#132e50', textTransform: 'uppercase', letterSpacing: 0.3 }}>CPS 230 Notification</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: tl.labelColor }}>{tl.label}</span>
      </div>
      <div style={{ height: 5, background: '#e8e3da', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#aaa', marginTop: 2 }}>
        <span>Occurred: {new Date(incident.occurred_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}</span>
        <span>72h deadline</span>
      </div>
    </div>
  );
}

function SummaryBar({ incidents }) {
  const total  = incidents.length;
  const open   = incidents.filter(i => i.status === 'open').length;
  const crit   = incidents.filter(i => i.severity === 'critical').length;
  const notify = incidents.filter(i => i.cps230_notification_required && !i.notified_at && i.status !== 'closed').length;
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', background: '#fff', border: '1px solid #e8e3da', borderRadius: 8, padding: '14px 20px', marginBottom: 24 }}>
      {[
        { label: 'Total incidents',   value: total,  color: '#132e50' },
        { label: 'Open',              value: open,   color: '#c62828' },
        { label: 'Critical',          value: crit,   color: '#b71c1c' },
        { label: 'Notification due',  value: notify, color: notify > 0 ? '#e65100' : '#2e7d32' },
      ].map(item => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: item.color }}>{item.value}</span>
          <span style={{ fontSize: 12, color: '#666' }}>{item.label}</span>
          <span style={{ color: '#ddd', marginLeft: 4 }}>|</span>
        </div>
      ))}
    </div>
  );
}

function IncidentCard({ incident, canEdit, onEdit, onDelete }) {
  const tl = cps230Timeline(incident);
  const typeEntry = INCIDENT_TYPES.find(t => t.value === incident.incident_type);
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e3da', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '14px 18px 10px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#132e50', background: '#eef1f6', border: '1px solid #c5cfe0', borderRadius: 4, padding: '2px 7px', letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
              {INCIDENT_TYPE_LABELS[incident.incident_type] || incident.incident_type}
            </span>
            {typeEntry && typeEntry.group !== 'CPS 230' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px' }}>
                {typeEntry.group}
              </span>
            )}
            <SeverityBadge severity={incident.severity} />
            <StatusBadge   status={incident.status} />
            {incident.cps230_notification_required && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#b8860b', background: '#fdf6e3', border: '1px solid #e8d49a', borderRadius: 4, padding: '2px 7px' }}>CPS 230</span>
            )}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 3 }}>{incident.title}</div>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
            {incident.description?.length > 140 ? incident.description.substring(0, 140) + '…' : incident.description}
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={() => onEdit(incident)} style={{ background: 'none', border: '1px solid #c5cfe0', borderRadius: 5, color: '#132e50', fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>Edit</button>
            <button onClick={() => onDelete(incident)} style={{ background: 'none', border: '1px solid #ef9a9a', borderRadius: 5, color: '#c62828', fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>Delete</button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 18px', background: '#faf9f7', borderTop: '1px solid #f0ece4', fontSize: 12, color: '#666' }}>
        <div><span style={{ color: '#999', marginRight: 4 }}>Occurred:</span><span style={{ color: '#333', fontWeight: 500 }}>{new Date(incident.occurred_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
        {incident.affected_system && <div><span style={{ color: '#999', marginRight: 4 }}>System:</span><span style={{ color: '#333', fontWeight: 500 }}>{incident.affected_system}</span></div>}
        {incident.vendor_name && <div><span style={{ color: '#999', marginRight: 4 }}>Vendor:</span><span style={{ color: '#333', fontWeight: 500 }}>{incident.vendor_name}</span></div>}
        <div><span style={{ color: '#999', marginRight: 4 }}>Owner:</span><span style={{ color: '#333', fontWeight: 500 }}>{incident.owner_name || 'Unassigned'}</span></div>
        {incident.status === 'closed' && incident.resolved_at && (
          <div><span style={{ color: '#999', marginRight: 4 }}>Resolved:</span><span style={{ color: '#2e7d32', fontWeight: 500 }}>{new Date(incident.resolved_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
        )}
      </div>
      {tl && incident.status !== 'closed' && (
        <div style={{ padding: '10px 18px 14px', borderTop: '1px solid #f0ece4', background: '#fdf9f2' }}>
          <Cps230Bar incident={incident} />
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#132e50', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };
const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #d8d3ca', borderRadius: 5, fontSize: 13, color: '#1a1a1a', background: '#fff', boxSizing: 'border-box', fontFamily: 'DM Sans, sans-serif' };
const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 72, lineHeight: 1.5 };

function IncidentModal({ incident, vendors, users, onClose, onSaved }) {
  const isEdit = !!incident?.id;
  const [form, setForm] = useState({
    title:                        incident?.title || '',
    incident_type:                incident?.incident_type || '',
    severity:                     incident?.severity || 'medium',
    affected_system:              incident?.affected_system || '',
    vendor_id:                    incident?.vendor_id || '',
    description:                  incident?.description || '',
    root_cause:                   incident?.root_cause || '',
    impact_description:           incident?.impact_description || '',
    cps230_notification_required: incident?.cps230_notification_required || false,
    notified_at:                  incident?.notified_at ? new Date(incident.notified_at).toISOString().substring(0,16) : '',
    notification_reference:       incident?.notification_reference || '',
    status:                       incident?.status || 'open',
    response_actions:             incident?.response_actions || '',
    resolution_notes:             incident?.resolution_notes || '',
    named_owner_id:               incident?.named_owner_id || '',
    occurred_at:                  incident?.occurred_at ? new Date(incident.occurred_at).toISOString().substring(0,16) : new Date().toISOString().substring(0,16),
  });
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const fileRef = useRef();
  function set(key, val) { setForm(prev => ({ ...prev, [key]: val })); }

  // Auto-suggest notification required for certain incident types
  function handleTypeChange(val) {
    set('incident_type', val);
    if (NOTIFICATION_REQUIRED_TYPES.includes(val) && !form.cps230_notification_required) {
      set('cps230_notification_required', true);
    }
  }

  async function handleSave() {
    if (!form.title.trim())       return setError('Title is required.');
    if (!form.incident_type)      return setError('Incident type is required.');
    if (!form.description.trim()) return setError('Description is required.');
    setSaving(true); setError('');
    try {
      const payload = { ...form, vendor_id: form.vendor_id || null, named_owner_id: form.named_owner_id || null, notified_at: form.notified_at || null, occurred_at: form.occurred_at || new Date().toISOString() };
      let saved;
      if (isEdit) { saved = await updateIncident(incident.id, payload); }
      else        { saved = await createIncident(payload); }
      if (fileRef.current?.files?.[0] && saved?.id) {
        setUploading(true);
        try { await uploadIncidentAttachment(saved.id, fileRef.current.files[0]); } catch (e) { console.warn('Attachment failed:', e.message); } finally { setUploading(false); }
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const notificationApplies = form.cps230_notification_required && ['critical', 'high'].includes(form.severity);

  // Group incident types for the select
  const typeGroups = ['CPS 230', 'CPS 234', 'AUSTRAC', 'FAR'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 680, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={{ background: '#132e50', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{isEdit ? 'Edit Incident' : 'Log New Incident'}</div>
            <div style={{ fontSize: 11, color: '#6b85a3', marginTop: 2 }}>AI-specific taxonomy — APRA CPS 230 · CPS 234 · AUSTRAC · FAR</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b85a3', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '24px', display: 'grid', gap: 16 }}>
          {error && <div style={{ background: '#fce4ec', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: 6, padding: '9px 14px', fontSize: 13 }}>{error}</div>}

          <div>
            <label style={labelStyle}>Incident title *</label>
            <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Brief, factual description of the incident" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Incident type *</label>
              <select style={inputStyle} value={form.incident_type} onChange={e => handleTypeChange(e.target.value)}>
                <option value="">— Select type —</option>
                {typeGroups.map(group => {
                  const groupTypes = INCIDENT_TYPES.filter(t => t.group === group);
                  return (
                    <optgroup key={group} label={group}>
                      {groupTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Severity *</label>
              <select style={inputStyle} value={form.severity} onChange={e => set('severity', e.target.value)}>
                {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Date and time occurred *</label>
              <input type="datetime-local" style={inputStyle} value={form.occurred_at} onChange={e => set('occurred_at', e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Affected AI system</label>
              <input style={inputStyle} value={form.affected_system} onChange={e => set('affected_system', e.target.value)} placeholder="System or model name" />
            </div>
            <div>
              <label style={labelStyle}>Vendor (if applicable)</label>
              <select style={inputStyle} value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
                <option value="">— No vendor linked —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description *</label>
            <textarea style={textareaStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What happened — factual, timestamped account suitable for audit" />
          </div>

          <div>
            <label style={labelStyle}>Root cause</label>
            <textarea style={{ ...textareaStyle, minHeight: 56 }} value={form.root_cause} onChange={e => set('root_cause', e.target.value)} placeholder="Known or suspected root cause" />
          </div>

          <div>
            <label style={labelStyle}>Impact description</label>
            <textarea style={{ ...textareaStyle, minHeight: 56 }} value={form.impact_description} onChange={e => set('impact_description', e.target.value)} placeholder="Customer, operational, or regulatory impact" />
          </div>

          <div>
            <label style={labelStyle}>Response actions taken</label>
            <textarea style={{ ...textareaStyle, minHeight: 56 }} value={form.response_actions} onChange={e => set('response_actions', e.target.value)} placeholder="Steps taken to contain or resolve the incident" />
          </div>

          {form.status === 'closed' && (
            <div>
              <label style={labelStyle}>Resolution notes</label>
              <textarea style={{ ...textareaStyle, minHeight: 56 }} value={form.resolution_notes} onChange={e => set('resolution_notes', e.target.value)} placeholder="How the incident was resolved and lessons learned" />
            </div>
          )}

          {/* Notification obligations */}
          <div style={{ background: '#fdf9f2', border: '1px solid #e8d49a', borderRadius: 7, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#132e50', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Notification Obligations</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={form.cps230_notification_required} onChange={e => set('cps230_notification_required', e.target.checked)} style={{ width: 15, height: 15 }} />
              <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>This incident triggers CPS 230 / CPS 234 / AUSTRAC notification obligations</span>
            </label>
            {notificationApplies && (
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 5, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#e65100', fontWeight: 500 }}>
                72-hour notification deadline applies. Deadline is calculated automatically from the occurrence date and time.
              </div>
            )}
            {form.cps230_notification_required && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Date and time notified</label>
                  <input type="datetime-local" style={inputStyle} value={form.notified_at} onChange={e => set('notified_at', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Notification reference</label>
                  <input style={inputStyle} value={form.notification_reference} onChange={e => set('notification_reference', e.target.value)} placeholder="APRA / AUSTRAC ticket or email ref" />
                </div>
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Named owner</label>
            <select style={inputStyle} value={form.named_owner_id} onChange={e => set('named_owner_id', e.target.value)}>
              <option value="">— Unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Supporting document (optional)</label>
            <input type="file" ref={fileRef} accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg" style={{ fontSize: 13, color: '#555' }} />
            <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>PDF, DOCX, XLSX, PNG, JPG — 20 MB max.</div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 5, padding: '8px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#555', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving || uploading} style={{ background: saving || uploading ? '#ccc' : '#132e50', border: 'none', borderRadius: 5, padding: '8px 24px', fontWeight: 700, fontSize: 13, cursor: saving || uploading ? 'not-allowed' : 'pointer', color: '#fff', fontFamily: 'DM Sans, sans-serif' }}>
              {uploading ? 'Uploading…' : saving ? 'Saving…' : isEdit ? 'Save changes' : 'Log incident'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Incidents({ user }) {
  const [incidents,       setIncidents]       = useState([]);
  const [vendors,         setVendors]         = useState([]);
  const [users,           setUsers]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [modal,           setModal]           = useState(null);
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [filterSeverity,  setFilterSeverity]  = useState('all');
  const [filterType,      setFilterType]      = useState('all');

  const canEdit = user && ['admin', 'compliance_manager'].includes(user.role);
  const isAdmin = user && user.role === 'admin';

  async function load() {
    setLoading(true); setError('');
    try {
      const [inc, vend, usr] = await Promise.all([
        getIncidents(),
        getVendors(),
        getUsers(),   // Fixed: was getOrgUsers (never exported)
      ]);
      setIncidents(Array.isArray(inc) ? inc : []);
      setVendors(Array.isArray(vend) ? vend : (vend?.vendors || []));
      setUsers(Array.isArray(usr) ? usr : (usr?.users || []));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(incident) {
    if (!window.confirm(`Delete incident "${incident.title}"? This action is logged and cannot be reversed.`)) return;
    try { await deleteIncident(incident.id); load(); } catch (e) { setError(e.message); }
  }

  const filtered = incidents.filter(i => {
    const matchStatus   = filterStatus   === 'all' || i.status        === filterStatus;
    const matchSeverity = filterSeverity === 'all' || i.severity      === filterSeverity;
    const matchType     = filterType     === 'all' || i.incident_type === filterType;
    return matchStatus && matchSeverity && matchType;
  });

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#132e50', margin: 0 }}>AI Incident Register</h1>
          <p style={{ marginTop: 6, color: '#666', fontSize: 14 }}>
            Structured log of AI-specific incidents — CPS 230, CPS 234, AUSTRAC, and FAR notification tracking.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setModal('create')} style={{ background: '#132e50', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'DM Sans, sans-serif' }}>
            + Log incident
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fce4ec', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 15 }}>Loading incidents…</div>
      ) : (
        <>
          <SummaryBar incidents={incidents} />

          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ ...labelStyle, display: 'inline', marginRight: 8 }}>Status:</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}>
                <option value="all">All statuses</option>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'inline', marginRight: 8 }}>Severity:</label>
              <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}>
                <option value="all">All severities</option>
                {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'inline', marginRight: 8 }}>Type:</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}>
                <option value="all">All types</option>
                {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #c5cfe0', borderRadius: 5, color: '#132e50', fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>↻ Refresh</button>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14, background: '#fff', border: '1px solid #e8e3da', borderRadius: 8 }}>
              {incidents.length === 0 ? 'No incidents logged yet. Use "Log incident" to record the first entry.' : 'No incidents match the selected filters.'}
            </div>
          ) : (
            filtered.map(i => <IncidentCard key={i.id} incident={i} canEdit={canEdit} onEdit={inc => setModal(inc)} onDelete={isAdmin ? handleDelete : () => {}} />)
          )}
        </>
      )}

      {modal && (
        <IncidentModal
          incident={modal === 'create' ? null : modal}
          vendors={vendors}
          users={users}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
